import React, { useEffect, useState } from 'react';
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  Paper,
  TableHead,
  Badge,
  MenuItem,
  Menu,
  Fade
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Delete, Edit, Remove, Warning } from '@material-ui/icons';
import { isNil, TRACK_RECORD_TEMPLATE, TRACK_RECORD_USER_INPUTTED } from '@roc/feature-utils';
import { ConfirmationIconButton, DialogState } from '@roc/ui';

const useTableStyles = makeStyles((theme) => {
  return {
    trackRecordGrid: (props: any) => ({
      boxShadow: 'none',
      border: '1px solid lightgrey',
      backgroundColor: 'transparent',
      '& tbody': {
        // border: 'solid lightgrey 1px',
        padding: '12px',
      },
      '& td': {
        borderBottom: '1px solid lightgrey',
        padding: '12px 0px 12px 12px',
      },
      ...(props.isExpandable && {
        '& tr:nth-child(even)': {
          borderBottom: 'solid lightgrey 1px',
        },
      }),
      ...(!props.isExpandable && {
        '& tr': {
          borderBottom: 'solid lightgrey 1px',
        },
      }),
    }),
    tableCellActions: {
      textAlign: 'center',
      padding: '12px 16px',
      // borderRight: '1px solid lightgray',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '150px',
    },
    tableCellLeftAlign: {
      textAlign: 'left',
      padding: '12px 16px',
      borderRight: '1px solid lightgray',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '150px',
      backgroundColor: '#EEEEEE'
    },
    tableCell: {
      textAlign: 'center',
      padding: '12px 16px',
      borderRight: '1px solid lightgray',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '150px',
      backgroundColor: '#EEEEEE'
    },
  };
});

const useDialogContentStyles = makeStyles(theme => ({
  root: {
    '& .dialog-content-box': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

export const TrackRecordTable = observer(({
  store,
  isExpandable = false,
  headers,
  rows,
  rowsPerPageOptions = [5, 10, 15],
  defaultRowsPerPage = 5,
  enablePagination = false,
  propertyButtons = null,
  borrowerBaseballCardStore
}) => {
  const classes = useTableStyles({ isExpandable });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    enablePagination ? defaultRowsPerPage : rows.length
  );

  const dialogContentclasses = useDialogContentStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    setRowsPerPage(
      enablePagination ? defaultRowsPerPage : rows.length
    );
  }, [enablePagination])

  const handleExpandClick = async (id, i, type, object) => {
    borrowerBaseballCardStore.propertyInformationDialogState = DialogState.EDIT;
    store.trackRecordPropertyFormStore.form.fields.screenType.value = 'addProperty';
    store.currentTasks = [];
    if (store.currentExpandedRowIndex === i) {
      store.setCurrentExpandedRowIndex(null);
      store.currentProperty = null;
    } else {
      store.setCurrentExpandedRowIndex(i);
      store.currentProperty = object;
      const taskEntityBody = {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'BORROWER_ENTITY_BORROWER',
        taskTemplateType: 'BORROWER_ENTITY_BORROWER',
        objectId: id,
      };

      const taskPropertyBody = {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'PROPERTY_TRACK_RECORD',
        taskTemplateType: 'PROPERTY_TRACK_RECORD',
        objectId: id,
      };

      const taskBody = type === 'entity' ? taskEntityBody : taskPropertyBody;
      await store.getTasks(taskBody);
      store.handleEditExistingTrackRecordProperty(object, i);
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => setSelectedRow(null), 200);
  };

  const getActions = (row, handleCopy, handleRequest) => [
    {
      icon: (
        <Badge badgeContent={row.unreadMessages} color="primary">
          <Edit color="primary" />
        </Badge>
      ),
      onClick: () => { },
      tooltip: "Copy Link"
    },
    {
      icon: <Remove color="primary" />,
      onClick: () => { },
      tooltip: "Request Payment"
    }
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    store.setCurrentExpandedRowIndex(null);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    store.setCurrentExpandedRowIndex(null);
  };

  const displayedRows = enablePagination
    ? rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    : rows;

  return (
    <div>
        <TableContainer className={classes.trackRecordGrid}>
          <Table>
          <TableHead>
            {!isNil(propertyButtons) && (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align="right" style={{ textAlign: 'right', padding: '8px' }}>
                  {propertyButtons()}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className={classes.tableCell}>Type of Deal</TableCell>
              <TableCell className={classes.tableCellLeftAlign}>Address</TableCell>
              <TableCell className={classes.tableCell}>Verification Status</TableCell>
              <TableCell className={classes.tableCell}>Purchase Date</TableCell>
              <TableCell className={classes.tableCell}>Purchase Amount</TableCell>
              <TableCell className={classes.tableCell}>Rehab Budget</TableCell>
              <TableCell className={classes.tableCell}>Seller Name</TableCell>
              <TableCell className={classes.tableCell}>Sale Date</TableCell>
              <TableCell className={classes.tableCell}>Sale Price</TableCell>
              <TableCell className={classes.tableCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
            <TableBody>
              {displayedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headers.length + (isExpandable ? 1 : 0)} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )
                : (
                  displayedRows.map((row, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        {row.cells.map((cell, cellIndex) => (
                          <TableCell key={cellIndex} width={headers[cellIndex].width}>{cell}</TableCell>
                        ))}
                        {isExpandable && row.expandableContent && (
                          <TableCell className={classes.tableCellActions} width='9%'>
                            {
                              !row?.object.isVerified && row?.object.recordSourceType && (row?.object.recordSourceType === TRACK_RECORD_USER_INPUTTED || row?.object.recordSourceType === TRACK_RECORD_TEMPLATE) &&
                              !row?.object.verificationInProgress &&
                              (<IconButton
                              aria-label="edit"
                              size="small"
                                onClick={async () => {
                                  await handleExpandClick(row.id, index, row.type, row.object);
                                }}
                            >
                                <Edit />
                              </IconButton>)}

                            <ConfirmationIconButton
                              testId="removeOption"
                              confirmDialogProps={{
                                title: 'Remove Track Record',
                                dialogContentclasses: dialogContentclasses,
                                body: (
                                  <>
                                    <Warning
                                      color="error"
                                      fontSize="large"
                                      style={{ marginRight: '16px' }}
                                    />
                                    <span>
                                      {`Are you sure you want to remove the record?`}
                                    </span>
                                  </>
                                ),
                                confirmButtonText: 'Yes',
                              }}
                              onClick={() => { store.handleRemoveTrackRecordProperty(row?.object) }}
                            >
                              <Delete />
                            </ConfirmationIconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
            </TableBody>
          </Table>

          {enablePagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
      </TableContainer>
    </div>
  );
});